import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useAuth } from "../../context/AuthContext";
import {
  getEvents,
  createEvent,
  updateEvent,
  deleteEvent,
  publishEvent,
  cancelEvent,
  uploadEventPhoto,
  getClubSettings,
  getClubCoaches,
  getClubTeams,
  type Event,
  type EventStatus,
  type ClubCoach,
} from "../../services/api";
import { getSports } from "../../services/sports";
import ClubAdminLayout from "../../layouts/ClubAdminLayout";
import { ClubNavIcon } from "../../layouts/clubNav";
import AdminPageHeader, { AdminButton } from "../../components/admin/AdminPageHeader";
import { AdminCard } from "../../components/admin/AdminCard";
import DateInput from "../../components/DateInput";

interface EventFormData {
  title: string;
  description: string;
  sport_id: string;
  location_address: string;
  location_city: string;
  location_county: string;
  start_date: string;
  end_date: string;
  event_time: string;
  specifications: string;
  auto_publish: boolean;
}

const defaultFormData: EventFormData = {
  title: "",
  description: "",
  sport_id: "",
  location_address: "",
  location_city: "",
  location_county: "",
  start_date: "",
  end_date: "",
  event_time: "",
  specifications: "",
  auto_publish: false,
};

export default function Events() {
  const navigate = useNavigate();
  const { activeClubId } = useAuth();
  const { t } = useTranslation();

  const statusLabels: Record<EventStatus, { label: string; color: string }> = {
    draft: { label: t("events.status_draft", "Ciornă"), color: "bg-slate-100 text-slate-700" },
    published: { label: t("events.status_published", "Publicat"), color: "bg-emerald-100 text-emerald-700" },
    cancelled: { label: t("events.status_cancelled", "Anulat"), color: "bg-red-100 text-red-700" },
    completed: { label: t("events.status_completed", "Finalizat"), color: "bg-blue-100 text-blue-700" },
  };

  const [events, setEvents] = useState<Event[]>([]);
  const [sports, setSports] = useState<{ id: string; name: string }[]>([]);
  const [coaches, setCoaches] = useState<ClubCoach[]>([]);
  const [teams, setTeams] = useState<{ id: string; name: string; age_group?: string | null; sport_type?: string | null }[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<EventStatus | "all">("all");

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [formData, setFormData] = useState<EventFormData>(defaultFormData);
  const [selectedCoachIds, setSelectedCoachIds] = useState<string[]>([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);

  // Delete confirmation
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  useEffect(() => {
    if (activeClubId) {
      fetchEvents();
      fetchSports();
      fetchCoaches();
      fetchTeams();
    }
  }, [activeClubId, statusFilter]);

  const fetchEvents = async () => {
    if (!activeClubId) {
      console.error("No activeClubId available");
      return;
    }
    setLoading(true);
    try {
      const status = statusFilter !== "all" ? statusFilter : undefined;
      const data = await getEvents(activeClubId, status);
      setEvents(data.events);
    } catch (error: any) {
      console.error("Failed to fetch events:", error);
      const errorMsg = error.response?.data?.error || t("events.error_load", "Nu s-au putut încărca evenimentele");
      toast.error(errorMsg);
    }
    setLoading(false);
  };

  const fetchCoaches = async () => {
    if (!activeClubId) return;
    try {
      const data = await getClubCoaches(activeClubId);
      setCoaches(data);
    } catch (error) {
      console.error("Failed to fetch coaches:", error);
    }
  };

  const fetchTeams = async () => {
    if (!activeClubId) return;
    try {
      setTeams(await getClubTeams(activeClubId));
    } catch (error) {
      console.error("Failed to fetch teams:", error);
    }
  };

  const fetchSports = async () => {
    if (!activeClubId) return;
    try {
      // Get club settings to see which sports are enabled for this club
      const [allSports, clubSettings] = await Promise.all([
        getSports(),
        getClubSettings(activeClubId),
      ]);
      // Filter sports to only those enabled for this club
      // clubSettings.sports contains sport IDs (UUIDs)
      const clubSportIds = new Set(clubSettings.sports || []);
      // If club has configured sports, show only those; otherwise show all sports.
      // Match on name as well as id: older saves stored sport NAMES in the same
      // column, and an id-only match left this dropdown completely empty.
      if (clubSportIds.size > 0) {
        const filteredSports = allSports.filter(
          (sport) => clubSportIds.has(sport.id) || clubSportIds.has(sport.name)
        );
        setSports(filteredSports.length > 0 ? filteredSports : allSports);
      } else {
        // No sports configured for club - show all available sports
        setSports(allSports);
      }
    } catch (error) {
      console.error("Failed to fetch sports:", error);
      // On error, try to at least show all sports
      try {
        const allSports = await getSports();
        setSports(allSports);
      } catch {
        // Ignore secondary error
      }
    }
  };

  const openCreateModal = () => {
    setEditingEvent(null);
    setFormData(defaultFormData);
    setSelectedCoachIds([]);
    setSelectedTeamIds([]);
    setPhotoFile(null);
    setPhotoPreview(null);
    setShowModal(true);
  };

  const openEditModal = (event: Event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description || "",
      sport_id: event.sport_id || "",
      location_address: event.location_address || "",
      location_city: event.location_city || "",
      location_county: event.location_county || "",
      start_date: event.start_date.split("T")[0],
      end_date: event.end_date.split("T")[0],
      event_time: event.event_time || "",
      specifications: event.specifications || "",
      auto_publish: false,
    });
    setSelectedCoachIds(
      (event as any).coaches?.map((ec: any) => ec.coach_id || ec.coach?.id) || []
    );
    setSelectedTeamIds(
      (event as any).teams?.map((et: any) => et.team_id || et.team?.id) || []
    );
    setPhotoFile(null);
    setPhotoPreview(event.photo_url);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingEvent(null);
    setFormData(defaultFormData);
    setSelectedCoachIds([]);
    setSelectedTeamIds([]);
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const toggleTeam = (teamId: string) => {
    setSelectedTeamIds((prev) =>
      prev.includes(teamId) ? prev.filter((id) => id !== teamId) : [...prev, teamId]
    );
  };

  const toggleCoach = (coachUserId: string) => {
    setSelectedCoachIds((prev) =>
      prev.includes(coachUserId)
        ? prev.filter((id) => id !== coachUserId)
        : [...prev, coachUserId]
    );
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(t("events.image_too_large", "Imaginea este prea mare (max 5MB)"));
        return;
      }
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeClubId) return;

    if (!formData.title || !formData.start_date || !formData.end_date) {
      toast.error(t("events.fill_required", "Completează câmpurile obligatorii"));
      return;
    }

    setSaving(true);
    try {
      let event: Event;
      const shouldAutoPublish = formData.auto_publish && !editingEvent;

      if (editingEvent) {
        event = await updateEvent(editingEvent.id, { ...formData, coach_ids: selectedCoachIds, team_ids: selectedTeamIds });
        toast.success(t("events.updated", "Eveniment actualizat!"));
      } else {
        event = await createEvent({
          club_id: activeClubId,
          ...formData,
          coach_ids: selectedCoachIds,
          team_ids: selectedTeamIds,
        });
        toast.success(t("events.created", "Eveniment creat!"));
      }

      // Upload photo if selected
      if (photoFile) {
        await uploadEventPhoto(event.id, photoFile);
      }

      // Auto-publish if checkbox was checked
      if (shouldAutoPublish) {
        await publishEvent(event.id);
        toast.success(t("events.published", "Eveniment publicat!"));
      }

      fetchEvents();
      closeModal();
    } catch (error: any) {
      console.error("Failed to save event:", error);
      toast.error(error.response?.data?.error || t("events.error_save", "Nu s-a putut salva evenimentul"));
    }
    setSaving(false);
  };

  const handlePublish = async (eventId: string) => {
    try {
      await publishEvent(eventId);
      toast.success(t("events.published", "Eveniment publicat!"));
      fetchEvents();
    } catch (error: any) {
      console.error("Failed to publish event:", error);
      toast.error(error.response?.data?.error || t("events.error_publish", "Nu s-a putut publica evenimentul"));
    }
  };

  const handleCancel = async (eventId: string) => {
    if (!window.confirm(t("events.confirm_cancel", "Sigur vrei să anulezi acest eveniment? Participanții vor fi notificați."))) return;
    try {
      await cancelEvent(eventId);
      toast.success(t("events.cancelled", "Eveniment anulat!"));
      fetchEvents();
    } catch (error: any) {
      console.error("Failed to cancel event:", error);
      toast.error(error.response?.data?.error || t("events.error_cancel", "Nu s-a putut anula evenimentul"));
    }
  };

  const handleDelete = async (eventId: string) => {
    if (deleteConfirmId !== eventId) {
      setDeleteConfirmId(eventId);
      setTimeout(() => setDeleteConfirmId(null), 3000);
      return;
    }
    try {
      await deleteEvent(eventId);
      toast.success(t("events.deleted", "Eveniment șters!"));
      fetchEvents();
    } catch (error: any) {
      console.error("Failed to delete event:", error);
      toast.error(error.response?.data?.error || t("events.error_delete", "Nu s-a putut șterge evenimentul"));
    }
    setDeleteConfirmId(null);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("ro-RO", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  if (loading && events.length === 0) {
    return (
      <ClubAdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-4 border-indigo-600 rounded-full animate-spin border-t-transparent"></div>
        </div>
      </ClubAdminLayout>
    );
  }

  return (
    <ClubAdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <AdminPageHeader
          title={t("events.title", "Evenimente")}
          subtitle={t("events.subtitle", "Gestionează evenimentele clubului și invitațiile")}
          icon={<ClubNavIcon id="events" className="w-6 h-6" />}
          actions={
            <AdminButton onClick={openCreateModal} icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>}>
              {t("events.new_event", "Eveniment nou")}
            </AdminButton>
          }
        />

        {/* Status Filter — slim toolbar; chips scroll horizontally on mobile so
            all 5 are reachable without wrapping onto multiple rows. */}
        <AdminCard className="p-3">
          <div className="flex gap-2 overflow-x-auto -mx-1 px-1 sm:mx-0 sm:px-0 sm:flex-wrap">
            {(["all", "draft", "published", "cancelled", "completed"] as const).map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`flex-shrink-0 px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  statusFilter === status
                    ? "bg-indigo-600 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {status === "all" ? t("common.all", "Toate") : statusLabels[status].label}
              </button>
            ))}
          </div>
        </AdminCard>

        {/* Events Grid */}
        {events.length === 0 ? (
          <AdminCard className="p-12 text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100">
              <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="mb-2 text-lg font-semibold text-slate-700">{t("events.no_events", "Niciun eveniment găsit")}</h3>
            <p className="mb-5 text-slate-500">{t("events.no_events_hint", "Creează primul eveniment pentru a începe")}</p>
            <button
              onClick={openCreateModal}
              className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-colors"
            >
              {t("events.create_first", "Creează primul eveniment")}
            </button>
          </AdminCard>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {events.map((event) => (
              <AdminCard
                key={event.id}
                className="overflow-hidden hover:shadow-md transition-shadow flex flex-col"
              >
                {/* Event Image */}
                <div className="h-40 bg-gradient-to-br from-blue-500 to-purple-600 relative">
                  {event.photo_url ? (
                    <img
                      src={event.photo_url}
                      alt={event.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <svg className="w-16 h-16 text-white/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                  <div className="absolute top-3 right-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold shadow-sm ${statusLabels[event.status].color}`}>
                      {statusLabels[event.status].label}
                    </span>
                  </div>
                </div>

                {/* Event Info */}
                <div className="p-4 flex flex-col flex-1">
                  <h3 className="text-[15px] font-bold text-slate-800 mb-3 truncate">{event.title}</h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="truncate">
                        {formatDate(event.start_date)}
                        {event.start_date !== event.end_date && ` - ${formatDate(event.end_date)}`}
                      </span>
                    </div>
                    {event.location_city && (
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        </svg>
                        <span className="truncate">{event.location_city}{event.location_county && `, ${event.location_county}`}</span>
                      </div>
                    )}
                    {event.sport && (
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        </svg>
                        <span className="truncate">{event.sport.name}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs font-medium text-slate-400 mt-3">
                    <span>{event._count?.invitations || 0} {t("events.invitations", "invitații")}</span>
                    <span>•</span>
                    <span>{event._count?.notes || 0} {t("events.notes_count", "note")}</span>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-3 mt-auto border-t border-slate-100">
                    <button
                      onClick={() => navigate(`/dashboard/admin-club/events/${event.id}`)}
                      className="flex-1 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
                    >
                      {t("common.details", "Detalii")}
                    </button>
                    {event.status === "draft" && (
                      <>
                        <button
                          onClick={() => openEditModal(event)}
                          className="px-3 py-2 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handlePublish(event.id)}
                          className="px-3 py-2 text-sm font-medium text-emerald-600 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors"
                          title={t("events.publish", "Publică")}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(event.id)}
                          className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                            deleteConfirmId === event.id
                              ? "bg-red-600 text-white"
                              : "text-red-600 bg-red-50 hover:bg-red-100"
                          }`}
                          title={deleteConfirmId === event.id ? t("events.click_to_confirm", "Click din nou pentru confirmare") : t("common.delete", "Șterge")}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </>
                    )}
                    {event.status === "published" && (
                      <button
                        onClick={() => handleCancel(event.id)}
                        className="px-3 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                        title={t("common.cancel", "Anulează")}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </AdminCard>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
          <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-800">
                {editingEvent ? t("events.edit_event", "Editează eveniment") : t("events.new_event", "Eveniment nou")}
              </h2>
              <button
                type="button"
                onClick={closeModal}
                className="p-2 -mr-2 rounded-lg text-slate-500 hover:bg-slate-100"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5">
              <div className="space-y-4">
                {/* Photo Upload */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("events.event_image", "Imagine eveniment")}
                  </label>
                  <div className="flex items-center gap-4">
                    <div className="w-32 h-20 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                      {photoPreview ? (
                        <img src={photoPreview} alt="Previzualizare" className="w-full h-full object-cover" />
                      ) : (
                        <div className="flex items-center justify-center h-full text-slate-300">
                          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <label className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 cursor-pointer transition-colors">
                      {t("events.select_image", "Selectează imagine")}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoChange}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("common.title", "Titlu")} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder={t("events.event_name_placeholder", "Numele evenimentului")}
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("common.description", "Descriere")}
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    rows={3}
                    placeholder={t("events.description_placeholder", "Descriere detaliată a evenimentului")}
                  />
                </div>

                {/* Sport */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("events.sport", "Sport")}
                  </label>
                  <select
                    value={formData.sport_id}
                    onChange={(e) => setFormData({ ...formData, sport_id: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="" className="text-slate-400">{t("events.select_sport", "Selectează sportul")}</option>
                    {sports.map((sport) => (
                      <option key={sport.id} value={sport.id} className="text-slate-700">{sport.name}</option>
                    ))}
                  </select>
                  {sports.length === 0 && (
                    <p className="mt-1 text-xs text-amber-600">
                      {t("events.no_sports_configured", "Nu sunt sporturi configurate pentru club. Adaugă sporturi din setările clubului.")}
                    </p>
                  )}
                </div>

                {/* Coaches */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("events.coaches", "Antrenori")}
                  </label>
                  {coaches.length === 0 ? (
                    <p className="text-xs text-amber-600">
                      {t("events.no_coaches", "Nu sunt antrenori în club.")}
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-40 overflow-y-auto border border-slate-200 rounded-xl p-3">
                      {coaches.map((coach) => (
                        <label
                          key={coach.user.id}
                          className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                            selectedCoachIds.includes(coach.user.id)
                              ? "bg-indigo-50 border border-indigo-200"
                              : "hover:bg-slate-50"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedCoachIds.includes(coach.user.id)}
                            onChange={() => toggleCoach(coach.user.id)}
                            className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-700 truncate">
                              {coach.user.full_name || coach.user.email}
                            </p>
                            {coach.teams.length > 0 && (
                              <p className="text-xs text-slate-400 truncate">
                                {coach.teams.map((t) => t.name).join(", ")}
                              </p>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                  {selectedCoachIds.length > 0 && (
                    <p className="mt-1 text-xs text-indigo-600">
                      {t("events.coaches_selected", "{{count}} antrenor(i) selectat(i)", { count: selectedCoachIds.length })}
                    </p>
                  )}
                </div>

                {/* Teams — optional, independent of the coach selection */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("events.teams", "Echipe")}{" "}
                    <span className="text-xs font-normal text-slate-400">{t("common.optional", "(opțional)")}</span>
                  </label>
                  {teams.length === 0 ? (
                    <p className="text-xs text-amber-600">{t("events.no_teams", "Nu sunt echipe în club.")}</p>
                  ) : (
                    <div className="space-y-2 max-h-40 overflow-y-auto border border-slate-200 rounded-xl p-3">
                      {teams.map((team) => (
                        <label
                          key={team.id}
                          className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                            selectedTeamIds.includes(team.id)
                              ? "bg-indigo-50 border border-indigo-200"
                              : "hover:bg-slate-50"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedTeamIds.includes(team.id)}
                            onChange={() => toggleTeam(team.id)}
                            className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-700 truncate">{team.name}</p>
                            {(team.age_group || team.sport_type) && (
                              <p className="text-xs text-slate-400 truncate">
                                {[team.age_group, team.sport_type].filter(Boolean).join(" · ")}
                              </p>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                  {selectedTeamIds.length > 0 && (
                    <p className="mt-1 text-xs text-indigo-600">
                      {t("events.teams_selected", "{{count}} echipă(e) selectată(e)", { count: selectedTeamIds.length })}
                    </p>
                  )}
                </div>

                {/* Dates */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("events.start_date", "Data început")} <span className="text-red-500">*</span>
                    </label>
                    <DateInput
                      value={formData.start_date}
                      onChange={(v) => setFormData({ ...formData, start_date: v })}
                      className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("events.end_date", "Data sfârșit")} <span className="text-red-500">*</span>
                    </label>
                    <DateInput
                      value={formData.end_date}
                      onChange={(v) => setFormData({ ...formData, end_date: v })}
                      min={formData.start_date}
                      className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                </div>

                {/* Time */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("events.event_time", "Ora evenimentului")}
                  </label>
                  <input
                    type="text"
                    value={formData.event_time}
                    onChange={(e) => setFormData({ ...formData, event_time: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="ex: 09:00 - 17:00"
                  />
                </div>

                {/* Location */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("events.address", "Adresă")}
                    </label>
                    <input
                      type="text"
                      value={formData.location_address}
                      onChange={(e) => setFormData({ ...formData, location_address: e.target.value })}
                      className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Strada, nr."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("events.city", "Oraș")}
                    </label>
                    <input
                      type="text"
                      value={formData.location_city}
                      onChange={(e) => setFormData({ ...formData, location_city: e.target.value })}
                      className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="București"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t("events.county", "Județ")}
                    </label>
                    <input
                      type="text"
                      value={formData.location_county}
                      onChange={(e) => setFormData({ ...formData, location_county: e.target.value })}
                      className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Sector 1"
                    />
                  </div>
                </div>

                {/* Specifications */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t("events.specifications", "Specificații")}
                  </label>
                  <textarea
                    value={formData.specifications}
                    onChange={(e) => setFormData({ ...formData, specifications: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    rows={3}
                    placeholder={t("events.specifications_placeholder", "Cerințe speciale, echipament necesar, etc.")}
                  />
                </div>

                {/* Auto-publish (only for new events) */}
                {!editingEvent && (
                  <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                    <input
                      type="checkbox"
                      id="auto_publish"
                      checked={formData.auto_publish}
                      onChange={(e) => setFormData({ ...formData, auto_publish: e.target.checked })}
                      className="w-5 h-5 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500"
                    />
                    <label htmlFor="auto_publish" className="flex-1 cursor-pointer">
                      <span className="font-medium text-emerald-800">{t("events.auto_publish", "Publică automat")}</span>
                      <p className="text-sm text-emerald-600">{t("events.auto_publish_desc", "Evenimentul va fi publicat imediat după creare")}</p>
                    </label>
                  </div>
                )}
              </div>
            </form>

            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-slate-100">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium text-sm"
              >
                {t("common.cancel", "Anulează")}
              </button>
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={saving}
                className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    {t("common.saving", "Se salvează...")}
                  </>
                ) : editingEvent ? (
                  t("events.save_changes", "Salvează modificările")
                ) : (
                  t("events.create_event", "Creează eveniment")
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </ClubAdminLayout>
  );
}
